import React, { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import {
  DataInputLabelButton,
  MenuItemsContainer,
  MenuItemsContainerInner,
} from '../../widget-calculator.styles';
import FormFieldText from '../../../../../common/form-field-text/form-field-text.component';
import { MenuItems } from '../../../../../common/menu-item/menu-item.styles';
import {
  FormValues,
  IDataInputNames,
} from '../../widget-calculator-form.component';
import MenuItem from '../../../../../common/menu-item/menu-item.component';
import { scrollInputToRight } from '../../../../../utils/forms/forms.utils';
import { CountryDetails } from '../../../../../models/shipmentCountryInfo';

interface Props {
  onFocus: () => void;
  onBlur: (e?: React.FocusEvent<HTMLInputElement>) => void;
  onDataInputLabelButtonClick: (inputName: IDataInputNames) => void;
  showInput: boolean;
  toggleInput: (arg: boolean) => void;
  getSearchOptions: (
    filter: string | undefined,
    departureCountry: CountryDetails | null
  ) => Promise<CountryDetails[]>;
  label: string;
  name: IDataInputNames;
  hiddenInputName: IDataInputNames;
  onSelect?: (option: CountryDetails) => void;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputCountryFormField = ({
  onFocus,
  onBlur,
  onDataInputLabelButtonClick,
  showInput,
  toggleInput,
  getSearchOptions,
  onSelect,
  onChange,
  label,
  name,
  hiddenInputName,
  disabled,
}: Props) => {
  const [searchOptions, setSearchOptions] = useState<
    CountryDetails[] | undefined
  >();
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [isBlurRequested, setIsBlurRequested] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const dropdownRef = useRef<HTMLInputElement | null>(null);

  const { setFieldValue, setFieldTouched, values, errors, getFieldMeta } =
    useFormikContext<FormValues>();

  const [isLabelClicked, setIsLabelClicked] = useState(false);

  const { value: fieldValue } = getFieldMeta<string>(name);

  function handleClickOutside(event: MouseEvent) {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      setShowSearchOptions(false);
      if (!inputRef.current?.value) {
        toggleInput(false);
      }
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownRef, toggleInput]);

  // Required to resume postponed blur after dropdown is closed and value is updated.
  useEffect(() => {
    if (isBlurRequested && !showSearchOptions) {
      onBlur();
      setIsBlurRequested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlurRequested, showSearchOptions]);

  useEffect(() => {
    if (!showInput) {
      inputRef?.current?.blur();
      return;
    }

    if (showInput && isLabelClicked) {
      inputRef?.current?.focus();
      setIsLabelClicked(false);
    }
  }, [isLabelClicked, showInput, searchOptions]);

  useEffect(() => {
    if (searchOptions?.length === 0) {
      setShowSearchOptions(false);
    } else if (document.activeElement === inputRef.current) {
      setShowSearchOptions(true);
    }
  }, [searchOptions]);

  const modifyListOfDisplayedOptions: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void = async (e) => {
    const { value } = e.target;
    setFieldTouched(name, true);
    setFieldTouched(hiddenInputName, true);

    setSearchOptions(await getSearchOptions(value, values.departure_country));
  };

  const handleSearchOptionSelected = (option: CountryDetails) => {
    setFieldValue(name, option.localized_name);

    setShowSearchOptions(false);
    onSelect?.(option);
  };

  useEffect(() => {
    async function updateSearchOptionsWhenDepartureCountryChanges() {
      const options = await getSearchOptions(
        fieldValue,
        values.departure_country
      );
      setSearchOptions(options);
    }

    updateSearchOptionsWhenDepartureCountryChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.departure_country, getSearchOptions]);

  return (
    <>
      <DataInputLabelButton
        $isInvalid={!!errors[name] || !!errors[hiddenInputName]}
        disabled={disabled}
        onClick={() => {
          onDataInputLabelButtonClick(name);
          setIsLabelClicked(true);
        }}>
        {t(label)}
      </DataInputLabelButton>

      <FormFieldText
        disabled={disabled}
        $mb='0.5rem'
        $borderNone
        ref={inputRef}
        validationMethod='always'
        label={t(label)}
        name={name}
        isHidden={!showInput}
        isLabelHidden
        onChange={(e) => {
          onChange && onChange(e);
          modifyListOfDisplayedOptions(e);
        }}
        type='text'
        placeholder={t('country')}
        showValidationError={false}
        onFocus={() => {
          onFocus();
          setShowSearchOptions(true);
          scrollInputToRight(inputRef);
        }}
        onBlur={(e) => {
          // ignore field blur while dropdown is opened to prevent unnecessary validation.
          if (showSearchOptions) {
            setIsBlurRequested(true);
            return;
          }
          onBlur(e);
        }}
      />
      {showSearchOptions && searchOptions && searchOptions.length > 0 && (
        <MenuItemsContainer
          ref={dropdownRef}
          onBlur={() => {
            setShowSearchOptions(false);
            if (!inputRef.current?.value) {
              toggleInput(false);
            }
          }}>
          <MenuItemsContainerInner>
            <MenuItems>
              {searchOptions.map((option) => (
                <MenuItem
                  key={option?.code}
                  useFlagEmojiFont={false}
                  onClick={() => handleSearchOptionSelected(option)}>
                  {option.localized_name}
                </MenuItem>
              ))}
            </MenuItems>
          </MenuItemsContainerInner>
        </MenuItemsContainer>
      )}
    </>
  );
};

export default observer(InputCountryFormField);
